import cls from "classnames";
import { STANDARD_EVENTS, trackStandardEvent } from "@qoohoo/common/utils/analytics/pixel";

import useSessionStore from "../../store/sessionStore";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import { button, link, pageview } from "@qoohoo/common/constants/analytics";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { addTagsInRecording } from "@qoohoo/common/utils/analytics/clarity";
import { getFormattedAmount } from "@qoohoo/common/utils/string";
import { MultiCurrency } from "@qoohoo/common/utils/currency/index";

const JoinButton = ({
  channelDetails,
  userChannelDetail,
  ctaButton,
  isCreator,
  isCoCreator,
  handleButtonClick,
  fullWidth = false,
  isLoading = false,
  isDisabled = false,
  couponDiscount,
  channelFee,
}) => {
  const userData = useSessionStore((state) => state.userData);
  const isChannelArchived = channelDetails.is_archived;
  const isChannelEnded = channelDetails.is_ended;

  const currency = channelDetails?.currency;
  
  const isRecurring = channelDetails.type === "recurring";
  const hasMultiplePlans = channelDetails?.plans?.length > 1;

  const isSubscribed = userChannelDetail.is_channel_member;

  const isButtonDisabled = isChannelArchived || isChannelEnded;

  const showStrikeThroughAmount = !hasMultiplePlans && couponDiscount;

  const trackDetails = {
    current_screen: pageview.channel_details,
    modal_name: "join_modal",
    uid: channelDetails?.uid,
    name: channelDetails?.name,
    "creator.uid": channelDetails?.creator?.uid,
    "creator.full_name": channelDetails?.creator?.fullname,
    "creator.username": channelDetails?.creator?.username,
    type: channelDetails?.type,
    recurring_type: channelDetails?.meta_info?.recurring_type,
    short_url_flutter: channelDetails?.short_url_flutter,
    category: channelDetails?.category?.name,
    is_paid: channelDetails?.is_paid,
    is_private: channelDetails?.is_private,
    has_limited_seats: channelDetails?.has_limited_seats,
    total_seats_left: channelDetails?.total_seats_left,
    channel_status: ctaButton.button_text,
  };

  function sendInitiateCheckoutEventViaPixel() {
    const paymentAmount = channelDetails?.is_paid
      ? channelFee
      : 0;

    if (channelDetails) {
      trackStandardEvent(
        STANDARD_EVENTS.InitiateCheckout,
        [channelDetails.uid],
        [{ id: channelDetails.uid, ...channelDetails }],
        paymentAmount
      );
    }
  }

  function getChannelFeeLabel() {
    let label = "";

    let finalAmount = channelFee;

    if (couponDiscount) {
      finalAmount = couponDiscount.amount_after_coupon_discount;
    }

    const formattedAmount = finalAmount? MultiCurrency.formatAmountWithSymbol(finalAmount, currency) : 0;

    if (isRecurring) {
      label = hasMultiplePlans
        ? "Subscribe Now"
        : `Subscribe for ${formattedAmount}`;
    } else if (finalAmount) {
      label = `Pay ${formattedAmount}`;
    } else {
      label = "Join for FREE";
    }

    return label;
  }

  function getButtonLabel() {
    let label = "";
    
    if(isChannelArchived)  {
      label = "Channel archived";
    } else if(isChannelEnded) {
      label = "Channel ended";
    } else if(isSubscribed) {
      label = "Extend Membership"
    } else if(isCreator || isCoCreator) {
      label = "Go to Channel"
    } else {
      label = getChannelFeeLabel();
    }

    return label;
  }

  if (isLoading) {
    return (
      <div
        className={cls(
          "flex items-center justify-center px-6 h-[52px] rounded-full text-lg font-semibold font-inter bg-[#1B1F22] md:max-w-none",
          fullWidth ? "w-full max-w-none" : "w-[200px] max-w-[200px]"
        )}
      >
        <Spinner size="sm" />
      </div>
    );
  }

  if (isButtonDisabled) {
    return (
      <div
        className={cls(
          "flex items-center justify-center w-full px-6 h-[52px] text-center rounded-full text-lg font-semibold font-inter bg-[#1B1F22] md:max-w-none outline-none focus:outline-none select-none cursor-not-allowed",
          fullWidth ? "w-full max-w-none" : "w-[200px] max-w-[200px]"
          )}>
        {getButtonLabel()}
      </div>
    );
  }

  if (isSubscribed) {
    return (
      <button
        onClick={() => {
          addTagsInRecording({ cta_btn: "pay" });
          Track.buttonClick(button.cta_btn, {
            ...trackDetails,
            label: "pay",
          });
          handleButtonClick();
        }}
        className={cls(
          "w-full px-6 h-[52px] rounded-full text-lg font-semibold font-inter hover:opacity-90 bg-[#5754F5]  md:max-w-none outline-none focus:outline-none",
          fullWidth ? "w-full max-w-none" : "w-[200px] max-w-[200px]"
        )}
      >
        {getButtonLabel()}
      </button>
    );
  }

  if (isCreator || isCoCreator) {
    return (
      <button
        onClick={() => {
          addTagsInRecording({ cta_btn: "go_to_channel" });
          Track.buttonClick(button.cta_btn, {
            ...trackDetails,
            label: "go_to_channel",
          });
          handleButtonClick(false);
        }}
        className={cls(
          "w-full px-6 h-[52px] rounded-full text-lg font-semibold font-inter hover:opacity-90 bg-white text-[#16191B] md:max-w-none outline-none focus:outline-none",
          fullWidth ? "w-full max-w-none" : "w-[200px] max-w-[200px]"
        )}
      >
        {getButtonLabel()}
      </button>
    );
  }

  return (
    <button
      onClick={() => {
        addTagsInRecording({ cta_btn: "join_now" });
        sendInitiateCheckoutEventViaPixel();
        Track.buttonClick(button.cta_btn, {
          ...trackDetails,
          label: "join_now",
        });
        handleButtonClick();
      }}
      disabled={isDisabled}
      className={cls(
        "px-6 h-[52px] rounded-full text-sm md:text-lg font-semibold font-inter hover:opacity-90 bg-[#5754F5] md:max-w-none relative",
        fullWidth ? "w-full max-w-none" : "w-[200px] max-w-[200px]",
        isDisabled && "opacity-60 hover:opacity-60 cursor-not-allowed"
      )}
    >
      {getButtonLabel()}
      {showStrikeThroughAmount && (
        <span className="line-through opacity-50 font-normal ml-2 absolute top-1/2 -translate-y-1/2 text-xs">
          {`${MultiCurrency.formatAmountWithSymbol(channelFee, currency)}`}
        </span>
      )}
    </button>
  );
};

export default JoinButton;
